import type { JsonObject } from 'type-fest'

/**
 * Lazy translation imports — each locale/namespace becomes a separate chunk
 * loaded on demand, only when the CDN backend fails.
 *
 * Previously these used `eager: true` which bundled ALL translations for all
 * locales into a single 634 KB (gzip) chunk loaded on every page.
 */

function mapNs(glob: Record<string, () => Promise<JsonObject>>) {
  const result: Record<string, () => Promise<JsonObject>> = {}
  for (const path in glob) {
    const ns = path.split('/').at(-1)!.slice(0, -'.json'.length)
    result[ns] = glob[path]
  }
  return result
}

const modules: Record<string, Record<string, () => Promise<JsonObject>>> = {
  en: mapNs(import.meta.glob<JsonObject>('./en/*.json', { import: 'default' })),
  ja: mapNs(import.meta.glob<JsonObject>('./ja/*.json', { import: 'default' })),
  zh: mapNs(import.meta.glob<JsonObject>('./zh/*.json', { import: 'default' })),
  ko: mapNs(import.meta.glob<JsonObject>('./kr/*.json', { import: 'default' })),
}

/**
 * Lazy-load a single bundled translation namespace.
 * Returns undefined if the language/namespace pair has no local bundle.
 *
 * NOTE: drift risk. Translators edit live in SimpleLocalize, so the JSON
 * checked in under `./{en,ja,zh,kr}/*.json` ages relative to what the CDN
 * serves. This is only the fallback (CDN failure / offline), so stale strings
 * are acceptable — but they ARE stale. If we want first-render strings to
 * match production, add a build-time `pnpm sync-i18n` that pulls from the
 * SimpleLocalize API and rewrites these files before bundling.
 */
export function loadBundledResource(
  language: string,
  namespace: string,
): Promise<JsonObject> | undefined {
  return modules[language]?.[namespace]?.()
}
